import { useState, type FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { Modal } from '@/components/common/Modal'
import { Field } from '@/components/common/Field'
import { inputClass, primaryButtonClass, selectClass } from '@/components/common/formStyles'
import { createAccount } from '@/lib/accountsApi'
import type { BrokerType } from '@/types/account'

interface AddAccountModalProps {
  open: boolean
  onClose: () => void
}

export function AddAccountModal({ open, onClose }: AddAccountModalProps) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [brokerType, setBrokerType] = useState<BrokerType>('ibkr')
  const [currency, setCurrency] = useState('USD')
  const [error, setError] = useState<string | null>(null)

  const reset = () => {
    setName('')
    setBrokerType('ibkr')
    setCurrency('USD')
    setError(null)
  }

  const mutation = useMutation({
    mutationFn: createAccount,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      reset()
      onClose()
    },
    onError: (err) => {
      if (isAxiosError(err) && err.response?.status === 422) {
        setError('Eingaben prüfen (Currency muss 3 Großbuchstaben sein).')
      } else {
        setError('Fehler beim Anlegen. Erneut versuchen.')
      }
    },
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    mutation.mutate({
      name: name.trim(),
      broker_type: brokerType,
      currency: currency.trim().toUpperCase(),
    })
  }

  const canSubmit = name.trim().length > 0 && currency.trim().length === 3 && !mutation.isPending

  return (
    <Modal open={open} onClose={onClose} title="Neuer Account">
      <form onSubmit={handleSubmit} className="flex flex-col gap-5" noValidate>
        <Field label="Name">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            required
            maxLength={128}
            className={inputClass}
          />
        </Field>

        <Field label="Broker">
          <select
            value={brokerType}
            onChange={(e) => setBrokerType(e.target.value as BrokerType)}
            className={selectClass}
          >
            <option value="ibkr">Interactive Brokers</option>
            <option value="consors">Consorsbank</option>
            <option value="scalable">Scalable Capital</option>
            <option value="manual">Manuell</option>
            <option value="external">External (BTC etc.)</option>
          </select>
        </Field>

        <Field label="Currency · ISO 4217">
          <input
            value={currency}
            onChange={(e) => setCurrency(e.target.value.toUpperCase())}
            maxLength={3}
            minLength={3}
            required
            className={`${inputClass} font-mono`}
          />
        </Field>

        {error && (
          <p role="alert" className="font-mono text-xs text-loss">
            {error}
          </p>
        )}

        <button type="submit" disabled={!canSubmit} className={primaryButtonClass}>
          {mutation.isPending ? 'Speichert' : 'Account anlegen'}
        </button>
      </form>
    </Modal>
  )
}
